/* Top-level error boundary for catastrophic crashes outside ConvexProvider.
   Submits bug reports via raw fetch to the /feedback HTTP endpoint since Convex hooks are unavailable. */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { CrashReportForm } from "@/components/auth/CrashReportForm";
import { Sentry } from "@/lib/sentry";

interface AppErrorBoundaryProps {
	children: ReactNode;
}

interface AppErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	showReportForm: boolean;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
	state: AppErrorBoundaryState = {
		hasError: false,
		error: null,
		showReportForm: false,
	};

	static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error("[AppErrorBoundary]", error, errorInfo);
		Sentry.captureException(error, {
			contexts: { react: { componentStack: errorInfo.componentStack ?? undefined } },
		});
	}

	handleRefresh = () => {
		window.location.reload();
	};

	handleShowReport = () => {
		this.setState({ showReportForm: true });
	};

	render() {
		if (this.state.hasError && this.state.error) {
			return (
				<div className="flex flex-col items-center justify-center min-h-screen bg-background px-4 text-center gap-6">
					<div className="space-y-2">
						<h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
						<p className="text-muted-foreground max-w-md">
							The app crashed unexpectedly. You can report this bug so we can fix it.
						</p>
					</div>

					{this.state.showReportForm ? (
						<CrashReportForm
							error={this.state.error}
							onDone={() => this.setState({ showReportForm: false })}
						/>
					) : (
						<div className="flex gap-2">
							<button
								type="button"
								onClick={this.handleShowReport}
								className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
							>
								Report this bug
							</button>
							<button
								type="button"
								onClick={this.handleRefresh}
								className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
							>
								Refresh page
							</button>
						</div>
					)}
				</div>
			);
		}

		return this.props.children;
	}
}
